// How bullet and numbered lists mark their items: the glyph catalogue, the
// presets, and the one resolver the editor and every export share, so a list
// reads the same on screen as in a .docx, .odt, PDF, EPUB or plain text file.
//
// A list stores at most two choices (see extensions/listStyles.ts):
//   preset   on the outermost list: the preset the whole list follows
//   marker   on any list: a style for that level alone, overriding the preset
// Everything else is worked out here from the writer's list settings.

export type ListKind = 'bullet' | 'ordered'
export type CounterFormat = 'decimal' | 'decimal-zero' | 'lower-alpha' | 'upper-alpha' | 'lower-roman' | 'upper-roman'
/** What goes around a number: 1.  1)  (1)  or the parent's numbers before it, 1.1 */
export type NumberStyle = 'period' | 'paren' | 'parens' | 'outline'
export type TaskBoxStyle = 'square' | 'round' | 'brackets'

/**
 * A marker as stored, a short string:
 *   n:<counter>:<style>   a numbered level, e.g. n:lower-alpha:parens → (a)
 *   b:<character>         a bullet, from the catalogue or the writer's own
 *   none                  no marker; the item is indented only
 */
export type MarkerSpec =
  | { kind: 'ordered'; counter: CounterFormat; style: NumberStyle }
  | { kind: 'bullet'; glyph: string }
  | { kind: 'none' }

export interface ListPreset {
  id: string
  name: string
  /** The kind of list it is for: a numbered preset is offered for numbered lists. */
  kind: ListKind
  /** Markers by depth, outermost first. */
  levels: string[]
  /** Past the last level: start the levels over, or keep repeating the last one. */
  after: 'cycle' | 'last'
  builtIn?: boolean
}

export interface ListPrefs {
  /** Preset ids new and unstyled lists follow. */
  numberedDefault: string
  bulletDefault: string
  taskBox: TaskBoxStyle
  /** Presets the writer made. */
  custom: ListPreset[]
}

// ── The writer's list settings, for code with no store at hand ───────────

let prefsSource: () => ListPrefs = () => DEFAULT_LIST_PREFS

/**
 * The settings store registers itself here, so the export generators, which
 * take only a document and their own settings, draw lists as the editor does.
 */
export function setListPrefsSource(source: () => ListPrefs) {
  prefsSource = source
}

export function currentListPrefs(): ListPrefs {
  return prefsSource()
}

// ── Catalogue ────────────────────────────────────────────────────────────

export const COUNTERS: { id: CounterFormat; label: string }[] = [
  { id: 'decimal', label: 'Numbers' },
  { id: 'decimal-zero', label: 'Leading zero' },
  { id: 'lower-alpha', label: 'Lower letters' },
  { id: 'upper-alpha', label: 'Upper letters' },
  { id: 'lower-roman', label: 'Lower roman' },
  { id: 'upper-roman', label: 'Upper roman' },
]

export const NUMBER_STYLES: { id: NumberStyle; label: string }[] = [
  { id: 'period', label: 'Period' },
  { id: 'paren', label: 'Parenthesis' },
  { id: 'parens', label: 'Both parentheses' },
  { id: 'outline', label: 'Outline' },
]

export interface GlyphGroup {
  id: string
  label: string
  glyphs: { char: string; name: string }[]
}

export const GLYPH_GROUPS: GlyphGroup[] = [
  { id: 'classic', label: 'Classic', glyphs: [
    { char: '•', name: 'Bullet' }, { char: '◦', name: 'White bullet' }, { char: '▪', name: 'Small square' },
  ] },
  { id: 'typewriter', label: 'Typewriter', glyphs: [
    { char: '*', name: 'Asterisk' }, { char: '–', name: 'En dash' }, { char: '›', name: 'Single guillemet' },
  ] },
  { id: 'geometric', label: 'Geometric', glyphs: [
    { char: '◆', name: 'Diamond' }, { char: '◇', name: 'White diamond' }, { char: '▸', name: 'Triangle' }, { char: '→', name: 'Arrow' },
  ] },
  { id: 'editorial', label: 'Editorial', glyphs: [
    { char: '¶', name: 'Pilcrow' }, { char: '§', name: 'Section' }, { char: '†', name: 'Dagger' }, { char: '❧', name: 'Hedera' },
  ] },
  { id: 'midcentury', label: 'Mid-century', glyphs: [
    { char: '✦', name: 'Four-pointed star' },
  ] },
]

/**
 * Glyphs drawn by Ink List Marks (scripts/build-list-marks-font.py) rather
 * than the line's font, which has no such characters (or, for the bullet, a
 * different size in every font): the same shape and size on every device.
 */
export const SHAPE_GLYPHS = new Set(['•', '◦', '▪', '◆', '◇', '▸', '→', '❧', '✦'])

// ── Presets ──────────────────────────────────────────────────────────────

export const BUILT_IN_PRESETS: ListPreset[] = [
  { id: 'standard-numbered', name: 'Standard', kind: 'ordered', after: 'cycle', builtIn: true,
    levels: ['n:decimal:period', 'n:lower-alpha:period', 'n:lower-roman:period'] },
  { id: 'outline', name: 'Outline', kind: 'ordered', after: 'cycle', builtIn: true,
    levels: ['n:upper-roman:period', 'n:upper-alpha:period', 'n:decimal:period', 'n:lower-alpha:period', 'n:lower-roman:period'] },
  { id: 'decimal', name: 'Decimal', kind: 'ordered', after: 'last', builtIn: true,
    levels: ['n:decimal:period', 'n:decimal:outline'] },
  { id: 'clause', name: 'Clause', kind: 'ordered', after: 'cycle', builtIn: true,
    levels: ['n:decimal:period', 'n:lower-alpha:parens', 'n:lower-roman:parens'] },
  { id: 'steps', name: 'Steps', kind: 'ordered', after: 'last', builtIn: true,
    levels: ['n:decimal:period', 'b:•', 'b:–'] },
  { id: 'standard-bullets', name: 'Standard', kind: 'bullet', after: 'cycle', builtIn: true,
    levels: ['b:•', 'b:◦', 'b:▪'] },
  { id: 'typewriter', name: 'Typewriter', kind: 'bullet', after: 'cycle', builtIn: true,
    levels: ['b:*', 'b:–', 'b:›'] },
  { id: 'dash', name: 'Dash', kind: 'bullet', after: 'cycle', builtIn: true,
    levels: ['b:–'] },
]

export const DEFAULT_LIST_PREFS: ListPrefs = {
  numberedDefault: 'standard-numbered',
  bulletDefault: 'standard-bullets',
  taskBox: 'square',
  custom: [],
}

const COUNTER_IDS = new Set(COUNTERS.map((c) => c.id))
const STYLE_IDS = new Set(NUMBER_STYLES.map((s) => s.id))

/** One user-perceived character, or null. */
export function singleGrapheme(value: string): string | null {
  const text = value.trim()
  if (!text) return null
  const Segmenter = (Intl as any).Segmenter
  if (Segmenter) {
    const parts = [...new Segmenter(undefined, { granularity: 'grapheme' }).segment(text)]
    return parts.length === 1 ? text : null
  }
  return [...text].length === 1 ? text : null
}

export function parseMarker(raw: unknown): MarkerSpec | null {
  if (typeof raw !== 'string' || !raw) return null
  if (raw === 'none') return { kind: 'none' }
  if (raw.startsWith('b:')) {
    const glyph = singleGrapheme(raw.slice(2))
    return glyph ? { kind: 'bullet', glyph } : null
  }
  const match = /^n:([a-z-]+):([a-z]+)$/.exec(raw)
  if (match && COUNTER_IDS.has(match[1] as CounterFormat) && STYLE_IDS.has(match[2] as NumberStyle)) {
    return { kind: 'ordered', counter: match[1] as CounterFormat, style: match[2] as NumberStyle }
  }
  return null
}

export function markerId(spec: MarkerSpec): string {
  if (spec.kind === 'none') return 'none'
  if (spec.kind === 'bullet') return `b:${spec.glyph}`
  return `n:${spec.counter}:${spec.style}`
}

function normalizePreset(raw: any): ListPreset | null {
  if (!raw || typeof raw.id !== 'string' || !raw.id) return null
  const levels = Array.isArray(raw.levels)
    ? raw.levels.filter((l: unknown) => parseMarker(l)).slice(0, 9)
    : []
  if (!levels.length) return null
  return {
    id: raw.id,
    name: typeof raw.name === 'string' && raw.name.trim() ? raw.name.trim().slice(0, 40) : 'Untitled',
    kind: raw.kind === 'bullet' ? 'bullet' : 'ordered',
    levels,
    after: raw.after === 'last' ? 'last' : 'cycle',
  }
}

export function normalizeListPrefs(raw: any): ListPrefs {
  const d = DEFAULT_LIST_PREFS
  const builtInIds = new Set(BUILT_IN_PRESETS.map((p) => p.id))
  const seen = new Set<string>()
  const custom: ListPreset[] = []
  for (const item of Array.isArray(raw?.custom) ? raw.custom : []) {
    const preset = normalizePreset(item)
    if (!preset || builtInIds.has(preset.id) || seen.has(preset.id)) continue
    seen.add(preset.id)
    custom.push(preset)
  }
  const prefs: ListPrefs = {
    numberedDefault: typeof raw?.numberedDefault === 'string' ? raw.numberedDefault : d.numberedDefault,
    bulletDefault: typeof raw?.bulletDefault === 'string' ? raw.bulletDefault : d.bulletDefault,
    taskBox: ['square', 'round', 'brackets'].includes(raw?.taskBox) ? raw.taskBox : d.taskBox,
    custom,
  }
  if (findPreset(prefs, prefs.numberedDefault)?.kind !== 'ordered') prefs.numberedDefault = d.numberedDefault
  if (findPreset(prefs, prefs.bulletDefault)?.kind !== 'bullet') prefs.bulletDefault = d.bulletDefault
  return prefs
}

export function allPresets(prefs: ListPrefs): ListPreset[] {
  return [...BUILT_IN_PRESETS, ...prefs.custom]
}

export function findPreset(prefs: ListPrefs, id: unknown): ListPreset | undefined {
  if (typeof id !== 'string' || !id) return undefined
  return BUILT_IN_PRESETS.find((p) => p.id === id) ?? prefs.custom.find((p) => p.id === id)
}

function defaultPreset(prefs: ListPrefs, kind: ListKind): ListPreset {
  const preset = findPreset(prefs, kind === 'ordered' ? prefs.numberedDefault : prefs.bulletDefault)
  if (preset && preset.kind === kind) return preset
  return BUILT_IN_PRESETS.find((p) => p.id === (kind === 'ordered' ? 'standard-numbered' : 'standard-bullets'))!
}

/** The marker a preset gives a level, counting the outermost list as depth 0. */
export function presetLevel(preset: ListPreset, depth: number): string {
  const { levels } = preset
  if (depth < levels.length) return levels[depth]
  return preset.after === 'last' ? levels[levels.length - 1] : levels[depth % levels.length]
}

// ── Counting ─────────────────────────────────────────────────────────────

function alpha(n: number): string {
  // 1 → a, 26 → z, 27 → aa, as word processors count.
  let out = ''
  while (n > 0) {
    n--
    out = String.fromCharCode(97 + (n % 26)) + out
    n = Math.floor(n / 26)
  }
  return out
}

function roman(n: number): string {
  if (n <= 0 || n >= 4000) return String(n)
  const table: [number, string][] = [
    [1000, 'm'], [900, 'cm'], [500, 'd'], [400, 'cd'], [100, 'c'], [90, 'xc'],
    [50, 'l'], [40, 'xl'], [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i'],
  ]
  let out = ''
  for (const [value, letters] of table) {
    while (n >= value) {
      out += letters
      n -= value
    }
  }
  return out
}

export function counterText(counter: CounterFormat, n: number): string {
  switch (counter) {
    case 'decimal-zero': return n >= 0 && n < 10 ? `0${n}` : String(n)
    case 'lower-alpha': return n > 0 ? alpha(n) : String(n)
    case 'upper-alpha': return n > 0 ? alpha(n).toUpperCase() : String(n)
    case 'lower-roman': return roman(n)
    case 'upper-roman': return roman(n).toUpperCase()
    default: return String(n)
  }
}

/** A sample of a marker for menus: the first item's, or its second level's for outline numbering. */
export function markerSample(spec: MarkerSpec): string {
  if (spec.kind === 'none') return '—'
  if (spec.kind === 'bullet') return spec.glyph
  const core = counterText(spec.counter, 1)
  return numberedText(spec.style, core, spec.style === 'outline' ? ['1'] : [])
}

function numberedText(style: NumberStyle, core: string, trail: string[]): string {
  switch (style) {
    case 'paren': return `${core})`
    case 'parens': return `(${core})`
    case 'outline': return trail.length ? [...trail, core].join('.') : `${core}.`
    default: return `${core}.`
  }
}

// ── Resolving a list ─────────────────────────────────────────────────────

export interface ListAttrs {
  preset?: unknown
  marker?: unknown
  /** Tiptap's own ordered-list type, set from pasted HTML: 1, a, A, i or I. */
  type?: unknown
  start?: unknown
  continue?: unknown
}

export interface ResolvedList {
  kind: ListKind
  /** How deep in its list tree, the outermost list being 0. */
  depth: number
  spec: MarkerSpec
  /** The number of the first item. */
  start: number
  /** Whether it carries on from the numbered list before it. */
  continued: boolean
  /** The numbers of the numbered items it sits inside, outermost first, for outline numbering. */
  trail: string[]
  /** The preset of its list tree. */
  preset: ListPreset
}

/** A list the walk is inside, and which of its items. */
export interface ListFrame {
  list: ResolvedList
  index: number
}

const HTML_TYPE_COUNTERS: Record<string, CounterFormat> = {
  '1': 'decimal', a: 'lower-alpha', A: 'upper-alpha', i: 'lower-roman', I: 'upper-roman',
}

function htmlTypeMarker(type: unknown): MarkerSpec | null {
  if (typeof type !== 'string') return null
  const counter = HTML_TYPE_COUNTERS[type]
  // A plain "1" is no choice at all; the preset decides.
  return counter && counter !== 'decimal' ? { kind: 'ordered', counter, style: 'period' } : null
}

export function listKindOf(typeName: string): ListKind | null {
  if (typeName === 'orderedList') return 'ordered'
  if (typeName === 'bulletList') return 'bullet'
  return null
}

/** A level's own marker, overriding its preset: chosen, or kept from pasted HTML. */
export function levelOverride(attrs: ListAttrs): MarkerSpec | null {
  return parseMarker(attrs.marker) ?? htmlTypeMarker(attrs.type)
}

/** The preset a list tree follows, given its outermost list. */
function treePreset(prefs: ListPrefs, rootKind: ListKind, rootAttrs: ListAttrs): ListPreset {
  const chosen = findPreset(prefs, rootAttrs.preset)
  return chosen && chosen.kind === rootKind ? chosen : defaultPreset(prefs, rootKind)
}

/**
 * The marker a level would have with no regard to the list's own kind: its
 * override, or its preset's level. Applying a preset uses this to turn a
 * nested list into a bullet or numbered list to match.
 */
export function wantedMarker(prefs: ListPrefs, kind: ListKind, attrs: ListAttrs, stack: ListFrame[]): MarkerSpec {
  const explicit = levelOverride(attrs)
  if (explicit) return explicit
  const preset = stack.length ? stack[0].list.preset : treePreset(prefs, kind, attrs)
  return parseMarker(presetLevel(preset, stack.length)) ?? { kind: 'bullet', glyph: '•' }
}

/**
 * The marker a list of this kind gets at this depth when what it would
 * otherwise show belongs to the other kind: a bullet list where its preset
 * wants numbers, say. It comes from the default preset for its kind.
 */
function naturalMarker(prefs: ListPrefs, kind: ListKind, depth: number): MarkerSpec {
  const preset = defaultPreset(prefs, kind)
  const level = parseMarker(presetLevel(preset, depth))
  if (level && level.kind === kind) return level
  for (const raw of preset.levels) {
    const spec = parseMarker(raw)
    if (spec && spec.kind === kind) return spec
  }
  return kind === 'ordered' ? { kind: 'ordered', counter: 'decimal', style: 'period' } : { kind: 'bullet', glyph: '•' }
}

/** Where numbering that carries on from the list before picks up. */
export interface ContinueState {
  /** The number after the last item of the most recent outermost numbered list. */
  next: number | null
}

export function resolveList(
  prefs: ListPrefs,
  typeName: string,
  attrs: ListAttrs,
  stack: ListFrame[],
  carry?: ContinueState,
): ResolvedList | null {
  const kind = listKindOf(typeName)
  if (!kind) return null
  const depth = stack.length
  const preset = depth ? stack[0].list.preset : treePreset(prefs, kind, attrs)
  let spec = wantedMarker(prefs, kind, attrs, stack)
  if (spec.kind !== 'none' && spec.kind !== kind) spec = naturalMarker(prefs, kind, depth)

  const ownStart = Number.parseInt(String(attrs.start ?? 1), 10)
  const continued = kind === 'ordered' && depth === 0 && attrs.continue === true && carry?.next != null
  const start = continued ? carry!.next! : Number.isFinite(ownStart) ? ownStart : 1

  // Each numbered level above, in its own counter: 1 → a → 3 gives "1.a.3".
  const trail: string[] = []
  for (const { list: outer, index } of stack) {
    if (outer.spec.kind === 'ordered') trail.push(counterText(outer.spec.counter, outer.start + index))
  }

  return { kind, depth, spec, start, continued, trail, preset }
}

export interface ItemMarker {
  text: string
  /** Drawn in Ink List Marks rather than the line's font. */
  shape: boolean
}

/** The marker of a list's item, counting from 0; null when the level shows none. */
export function itemMarker(list: ResolvedList, index: number): ItemMarker | null {
  const { spec } = list
  if (spec.kind === 'none') return null
  if (spec.kind === 'bullet') return { text: spec.glyph, shape: SHAPE_GLYPHS.has(spec.glyph) }
  const core = counterText(spec.counter, list.start + index)
  return { text: numberedText(spec.style, core, spec.style === 'outline' ? list.trail : []), shape: false }
}

export interface ListVisit {
  node: any
  list: ResolvedList
  /** The lists it sits inside, outermost first. */
  ancestors: { node: any; list: ResolvedList }[]
}

/**
 * Walks every bullet and numbered list in a document given as Tiptap JSON,
 * in order, for the exports. Task lists are passed through: a list inside a
 * task item counts its depth from the lists around the task list.
 */
export function walkDocLists(doc: any, prefs: ListPrefs, visit: (found: ListVisit) => void) {
  const carry: ContinueState = { next: null }

  function walk(node: any, stack: ListFrame[], ancestors: ListVisit['ancestors']) {
    if (!node || typeof node !== 'object') return
    const list = resolveList(prefs, node.type, node.attrs || {}, stack, carry)
    const children: any[] = Array.isArray(node.content) ? node.content : []
    if (!list) {
      for (const child of children) walk(child, stack, ancestors)
      return
    }
    visit({ node, list, ancestors })
    const inside = [...ancestors, { node, list }]
    children.forEach((item, index) => {
      const frame = { list, index }
      for (const child of item?.content || []) walk(child, [...stack, frame], inside)
    })
    if (list.kind === 'ordered' && list.depth === 0) carry.next = list.start + children.length
  }

  walk(doc, [], [])
}

/** Every bullet and numbered list in a Tiptap JSON document, resolved. */
export function resolveDocLists(doc: any, prefs: ListPrefs): WeakMap<object, ResolvedList> {
  const resolved = new WeakMap<object, ResolvedList>()
  walkDocLists(doc, prefs, ({ node, list }) => resolved.set(node, list))
  return resolved
}

/** The first item's marker at each of a preset's first levels, for menus. */
export function presetPreview(prefs: ListPrefs, preset: ListPreset, levels = 3): (ItemMarker | null)[] {
  const scoped: ListPrefs = findPreset(prefs, preset.id) === preset ? prefs : { ...prefs, custom: [...prefs.custom.filter((p) => p.id !== preset.id), preset] }
  const stack: ListFrame[] = []
  const out: (ItemMarker | null)[] = []
  for (let depth = 0; depth < levels; depth++) {
    const spec = parseMarker(presetLevel(preset, depth))
    const kind: ListKind = depth === 0 ? preset.kind : spec?.kind === 'bullet' || spec?.kind === 'ordered' ? spec.kind : stack[stack.length - 1].list.kind
    const list = resolveList(scoped, kind === 'ordered' ? 'orderedList' : 'bulletList', depth === 0 ? { preset: preset.id } : {}, stack)
    if (!list) break
    out.push(itemMarker(list, 0))
    stack.push({ list, index: 0 })
  }
  return out
}

/**
 * How far a level's text should hang from its marker, in ems of the body
 * text, for exports that set the indent ahead (Word, OpenDocument): room for
 * a typical marker of the level and a gap, never less than a word
 * processor's usual hanging indent.
 */
export function markerHangEm(spec: MarkerSpec, trail: string[] = []): number {
  let chars = 1
  if (spec.kind === 'ordered') {
    const core = spec.counter === 'lower-roman' || spec.counter === 'upper-roman' ? 3 : spec.counter === 'lower-alpha' || spec.counter === 'upper-alpha' ? 1 : 2
    const punctuation = spec.style === 'parens' ? 2 : spec.style === 'outline' && trail.length ? 0 : 1
    const above = spec.style === 'outline' ? trail.reduce((sum, part) => sum + part.length + 1, 0) : 0
    chars = core + punctuation + above
  }
  return Math.max(1.5, chars * 0.55 + 0.7)
}
